import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { endOfDay, format, startOfDay, subDays } from "date-fns"
import * as fileDownload from "js-file-download"
import { useCallback, useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalLinearProgress from "../../../common/components/ConditionalLinearProgress"
import { RowButColumnOnMobile } from "../../../common/components/Row"
import NoResultsTableRow from "../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../common/components/tables/TablePaginationRow"
import {
  HeaderTableCell,
  NoWrapTableCell,
} from "../../../common/components/tables/cells"
import { usePaginationState } from "../../../common/components/tables/utils"

function formatReportDate(dateStr) {
  if (dateStr == null || dateStr === "") {
    return "—"
  }
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) {
    return "—"
  }
  return format(date, "MMM d yyyy")
}

/** Reports uploaded to the account; rendered as a tab on the Activity page. */
export default function ReportsPanel() {
  const axios = useAuthorizedAxiosClient()
  const [reports, setReports] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [processingReportId, setProcessingReportId] = useState(null)
  const [dateFrom, setDateFrom] = useState(() =>
    startOfDay(subDays(new Date(), 30))
  )
  const [dateTo, setDateTo] = useState(() => endOfDay(new Date()))
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState(20)

  useEffect(() => {
    setIsLoading(true)
    const params = {
      page: pageIndex,
      page_size: pageSize,
    }
    if (dateFrom) {
      params.date_from = dateFrom.toISOString()
    }
    if (dateTo) {
      params.date_to = dateTo.toISOString()
    }

    axios
      .get("/crm/api/reports/", { params })
      .then(({ data }) => {
        setReports(data.results ?? [])
        setTotalResultsSize(data.count ?? 0)
      })
      .catch(() => {
        setReports([])
        setTotalResultsSize(0)
      })
      .finally(() => setIsLoading(false))
  }, [axios, dateFrom, dateTo, pageIndex, pageSize, setTotalResultsSize])

  const handleDownload = useCallback(
    (report) => {
      setProcessingReportId(report.id)
      axios
        .get(`/crm/api/reports/${report.id}/download/`, {
          responseType: "blob",
        })
        .then(({ data }) => fileDownload(data, report.original_filename))
        .finally(() => setProcessingReportId(null))
    },
    [axios]
  )

  const handleView = useCallback(
    (report) => {
      setProcessingReportId(report.id)
      axios
        .get(`/crm/api/reports/${report.id}/download/`, {
          responseType: "blob",
          params: { inline: true },
        })
        .then(({ data }) => {
          const blob = new Blob([data], {
            type: report.content_type || "application/pdf",
          })
          const objectUrl = URL.createObjectURL(blob)
          window.open(objectUrl, "_blank", "noopener,noreferrer")
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
        })
        .finally(() => setProcessingReportId(null))
    },
    [axios]
  )

  function handleDateFromChanged(date) {
    if (!date) {
      return
    }
    setDateFrom(startOfDay(date))
    setPageIndex(1)
  }

  function handleDateToChanged(date) {
    if (!date) {
      return
    }
    setDateTo(endOfDay(date))
    setPageIndex(1)
  }

  return (
    <Column sx={{ height: "100%" }}>
      <ConditionalLinearProgress show={isLoading} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View and download reports uploaded to your account.
      </Typography>
      <RowButColumnOnMobile sx={{ gap: 2, mb: 2, alignItems: "center" }}>
        <CalendarTodayOutlinedIcon sx={{ color: "colors.gray700" }} />
        <DatePicker
          label="From"
          value={dateFrom}
          onChange={handleDateFromChanged}
          maxDate={dateTo}
          slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
        />
        <DatePicker
          label="To"
          value={dateTo}
          onChange={handleDateToChanged}
          minDate={dateFrom}
          maxDate={new Date()}
          slotProps={{ textField: { size: "small", sx: { minWidth: 160 } } }}
        />
      </RowButColumnOnMobile>
      <ScrollableTableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <HeaderTableCell>Title</HeaderTableCell>
              <HeaderTableCell>Uploaded</HeaderTableCell>
              <HeaderTableCell align="right">Actions</HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {Array.from({ length: 3 }).map((__, cellIndex) => (
                    <NoWrapTableCell key={cellIndex}>
                      <Skeleton />
                    </NoWrapTableCell>
                  ))}
                </TableRow>
              ))}
            {!isLoading && reports.length === 0 && (
              <NoResultsTableRow
                colSpan={3}
                message="No reports in this date range."
              />
            )}
            {!isLoading &&
              reports.map((report) => (
                <TableRow key={report.id} hover>
                  <NoWrapTableCell>
                    <Typography variant="body2">{report.title}</Typography>
                  </NoWrapTableCell>
                  <NoWrapTableCell>
                    {formatReportDate(report.created)}
                  </NoWrapTableCell>
                  <NoWrapTableCell align="right">
                    <ButtonGroup
                      variant="outlined"
                      color="inherit"
                      size="small"
                    >
                      <Button
                        startIcon={<VisibilityOutlinedIcon />}
                        disabled={processingReportId === report.id}
                        onClick={() => handleView(report)}
                      >
                        View
                      </Button>
                      <Button
                        startIcon={<FileDownloadOutlinedIcon />}
                        disabled={processingReportId === report.id}
                        onClick={() => handleDownload(report)}
                      >
                        Download
                      </Button>
                    </ButtonGroup>
                  </NoWrapTableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </ScrollableTableContainer>
      <TablePaginationRow
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        tableRows={reports}
        totalResultsSize={totalResultsSize}
      />
    </Column>
  )
}
